import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, MapPin, Star, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { initials, resolveImg } from "@/lib/format";

export default function Universities() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/public/universities", { params: { q: search, page, per_page: 12 } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [search, page]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Universities & Colleges</h1>
      <p className="mt-1 text-slate-500">Browse institutions offering online programmes</p>

      <form onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(q); }} className="mt-6 flex max-w-md gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3">
          <SearchIcon className="h-5 w-5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search universities..."
            className="border-0 shadow-none focus-visible:ring-0" data-testid="uni-search-input" />
        </div>
        <Button type="submit" className="bg-blue-800 hover:bg-blue-900">Search</Button>
      </form>

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {(data?.items || []).map((u) => (
          <Link key={u.id} to={`/universities/${u.slug}`} data-testid={`uni-card-${u.id}`}
            className="group flex flex-col rounded-xl border border-slate-200 bg-white p-6 transition-all hover:shadow-lg">
            <div className="flex items-center gap-3">
              {u.logo ? <img src={resolveImg(u.logo)} alt={u.name} className="h-12 w-12 rounded-md object-cover" />
                : <span className="flex h-12 w-12 items-center justify-center rounded-md bg-blue-50 font-heading font-bold text-blue-800">{initials(u.name)}</span>}
              <div>
                <h3 className="font-heading font-semibold text-slate-900 group-hover:text-blue-800">{u.name}</h3>
                <p className="flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3" />{[u.city, u.country].filter(Boolean).join(", ")}</p>
              </div>
              {u.featured && <Star className="ml-auto h-4 w-4 fill-sky-500 text-sky-500" />}
            </div>
            <p className="mt-3 line-clamp-2 text-sm text-slate-500">{u.description}</p>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-slate-500">{u.course_count} programmes</span>
              <span className="font-medium text-blue-800">View <ArrowRight className="inline h-4 w-4" /></span>
            </div>
          </Link>
        ))}
      </div>

      {data && data.total > data.per_page && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="px-3 text-sm text-slate-600">Page {page} of {Math.ceil(data.total / data.per_page)}</span>
          <Button variant="outline" disabled={page >= Math.ceil(data.total / data.per_page)} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
